import type {
  CelicaMatrike,
  IzracunObcutljivosti,
  NacinMatrike,
  Obcutljivost,
  ObdobjeObcutljivosti,
  StrosekObcutljivosti,
  VrstaObdobjaObcutljivosti,
} from '@koda/shared';
import { useEffect, useState, type JSX, type ReactNode } from 'react';

import { api } from '../lib/api';
import { decimalno, evri, znesek } from '../lib/format';
import { useBesedila, useJezik } from '../lib/jezik';
import { useTelefon } from '../lib/telefon';
import { GlavaKazalnikov } from './GlavaKazalnikov';
import { Lupa, obdobjeLeta } from './kazalniki-skupno';

interface Lastnosti {
  podjetje: string;
  naslov: string;
  /** Stikalo Pregled · Podrobno · Občutljivost (v glavi desno). */
  stikaloPogleda: ReactNode;
  obdobje: VrstaObdobjaObcutljivosti | undefined;
  naObdobje: (o: VrstaObdobjaObcutljivosti) => void;
  strosek: string | undefined;
  naStrosek: (konto: string) => void;
  nacin: NacinMatrike;
  naNacin: (n: NacinMatrike) => void;
}

/** Odstotek iz deleža 0–1, npr. "0.1225" → »12,25 %«. */
const odstotekDeleza = (delez: string, decimalk: number): string => `${decimalno(String(Number(delez) * 100), decimalk)} %`;

/** Sprememba osi matrike z izrecnim predznakom: »+5 %«, »−2,5 %«, »0«. */
function sprememba(v: string): string {
  // Na desetinko odstotka, da plavajoča vejica (0,07 · 100) ne da »7,0 %«.
  const x = Math.round(Number(v) * 1000) / 10;
  if (x === 0) return '0';
  const decimalk = Number.isInteger(x) ? 0 : 1;
  return `${x > 0 ? '+' : '−'}${decimalno(String(Math.abs(x)), decimalk)} %`;
}

/** Del faktorja »(1 + 5 %)«: »+ 5 %«, »− 2,5 %«, »+ 0 %«. */
function faktor(v: string): string {
  const x = sprememba(v);
  return x === '0' ? '+ 0 %' : `${x.charAt(0)} ${x.slice(1)}`;
}

/** Razlika v celih evrih z izrecnim predznakom. */
function razlika(v: number): string {
  const r = Math.round(v);
  if (r === 0) return '0 €';
  return `${r > 0 ? '+' : '−'}${evri(String(Math.abs(r)))}`;
}

/** Kratek zapis za osi grafa: »2,0 mio«, »250 k«. */
function kratko(v: number): string {
  if (Math.abs(v) >= 1e6) return `${decimalno(String(v / 1e6), 1)} mio`;
  if (Math.abs(v) >= 1e3) return `${decimalno(String(v / 1e3), 0)} k`;
  return decimalno(String(v), 0);
}

/** Lep korak osi (1, 2 ali 5 × 10^k) za približno štiri razdelke. */
function korak(razpon: number): number {
  if (razpon <= 0) return 1;
  const surov = razpon / 4;
  const red = 10 ** Math.floor(Math.log10(surov));
  const m = surov / red;
  return (m <= 1 ? 1 : m <= 2 ? 2 : m <= 5 ? 5 : 10) * red;
}

/** Oznaka obdobja v izbirniku in podnaslovu. */
function useOznakaObdobja(): (o: ObdobjeObcutljivosti, dolga: boolean) => string {
  const b = useBesedila();
  return (o, dolga) => {
    if (o.vrsta === 'celo') return dolga ? b.ob.podnaslovCelo(o.leto) : b.ob.celoLeto(o.leto);
    const obdobje = obdobjeLeta(o.doMeseca, o.leto, b.meseci);
    return dolga ? b.ob.podnaslovTekoce(obdobje) : b.ob.tekoceLeto(obdobje.charAt(0).toLowerCase() + obdobje.slice(1));
  };
}

/**
 * Zavihek Občutljivost strani Kazalniki (docs/ARHITEKTURA.md, 6.3): glava z
 * obdobjem, opozorilo o nerazvrščenih, ocenjena točka preloma z grafom in
 * izračunom desno ob grafu, matrika EBITDA z izbiro stroška in načina nad
 * njo. Vse številke računa strežnik; vsaka ima pot do knjižb.
 */
export function ObcutljivostKazalnikov(p: Lastnosti): JSX.Element {
  const b = useBesedila();
  const jezik = useJezik();
  const oznaka = useOznakaObdobja();
  const [odgovor, setOdgovor] = useState<Obcutljivost | null>(null);
  const [napaka, setNapaka] = useState<string | null>(null);

  useEffect(() => {
    let velja = true;
    setNapaka(null);
    api
      .obcutljivost(p.podjetje, jezik, p.obdobje, p.strosek)
      .then((o) => {
        if (velja) setOdgovor(o);
      })
      .catch((e: unknown) => {
        if (velja) setNapaka(e instanceof Error ? e.message : b.porociloNiMogoce);
      });
    return () => {
      velja = false;
    };
  }, [p.podjetje, jezik, p.obdobje, p.strosek]);

  const o = odgovor;
  const podnaslov = o?.obdobje ? oznaka(o.obdobje, true) : '';
  const n = o?.nerazvrsceni ?? null;

  return (
    <div className="kz-stran obcutljivost">
      <GlavaKazalnikov naslov={p.naslov} podnaslov={podnaslov}>
        {o !== null && o.obdobja.length > 1 && o.obdobje !== null && (
          <label className="kz-kontrola">
            <span className="kz-kontrola-naziv">{b.ob.obdobje}</span>
            <select
              value={o.obdobje.vrsta}
              onChange={(e) => {
                p.naObdobje(e.target.value as VrstaObdobjaObcutljivosti);
              }}
            >
              {o.obdobja.map((x) => (
                <option key={x.vrsta} value={x.vrsta}>
                  {oznaka(x, false)}
                </option>
              ))}
            </select>
          </label>
        )}
        {p.stikaloPogleda}
      </GlavaKazalnikov>

      {napaka !== null && <p className="napaka">{napaka}</p>}
      {o === null && napaka === null && <p>{b.racunamPorocilo}</p>}
      {o?.opozorila.map((x) => (
        <p key={x} className="opozorilo">
          {x}
        </p>
      ))}

      {o !== null && n !== null && n.stevilo > 0 && (
        <section className="kz-karta fcvc-opozorilo" aria-label={b.ob.nadPragomNaslov}>
          <p>
            <Lupa /> {b.ob.nerazvrsceni(odstotekDeleza(n.delez ?? '0', 1), evri(n.znesek))}
          </p>
          <ul>
            {n.najvecji.map((k) => (
              <li key={k.konto}>
                <code>{k.konto}</code> {k.naziv} · {evri(k.znesek)}
              </li>
            ))}
          </ul>
        </section>
      )}

      {o?.razlog === 'nad_pragom' && n !== null && (
        <section className="kz-karta">
          <h2>{b.ob.nadPragomNaslov}</h2>
          <p>{b.ob.nadPragomBesedilo(odstotekDeleza(n.prag, 0))}</p>
        </section>
      )}
      {o?.razlog === 'kontrola' && o.kontrola !== null && (
        <p className="napaka">{b.ob.kontrola(znesek(o.kontrola.ebitda), znesek(o.kontrola.celotniDobicek))}</p>
      )}
      {o?.razlog === 'ni_podatkov' && <p>{b.ob.niPodatkov}</p>}

      {o?.izracun != null && o.obdobje !== null && (
        <>
          <TockaPreloma izracun={o.izracun} />
          <MatrikaEbitda
            izracun={o.izracun}
            nacin={p.nacin}
            naNacin={p.naNacin}
            naStrosek={p.naStrosek}
          />
        </>
      )}
    </div>
  );
}

function TockaPreloma({ izracun: i }: { izracun: IzracunObcutljivosti }): JSX.Element {
  const b = useBesedila();
  const [odprt, setOdprt] = useState(false);
  const t = i.tocka;
  const stavek =
    t.stanje === 'ni_mogoce'
      ? b.ob.stavekNiMogoce
      : t.stanje === 'pokrito'
        ? b.ob.stavekPokrito
        : t.stanje === 'primanjkljaj'
          ? b.ob.stavekPrimanjkljaj(evri(String(-Number(t.rezerva))))
          : b.ob.stavekRezerva(odstotekDeleza(t.rezervaDelez ?? '0', 2));
  const df = Number(i.df);
  const vrstice: { naziv: string; vrednost: string; podvrstica?: boolean }[] = [
    { naziv: b.ob.izracunCp, vrednost: znesek(i.cp) },
    { naziv: b.ob.izracunV, vrednost: znesek(i.variabilni) },
    { naziv: b.ob.izracunCm, vrednost: t.cm === null ? '—' : odstotekDeleza(t.cm, 2) },
    { naziv: b.ob.izracunFe, vrednost: znesek(i.fiksni) },
    { naziv: b.ob.izracunAm, vrednost: znesek(i.am) },
    // Drugi poslovni prihodki so izpeljani: poslovni prihodki − čisti prihodki.
    { naziv: b.ob.izracunOr, vrednost: znesek(i.or) },
    { naziv: b.ob.izracunPoslovni, vrednost: znesek(i.poslovniPrihodki), podvrstica: true },
    { naziv: b.ob.izracunOdstetiCp, vrednost: znesek(i.cp), podvrstica: true },
    { naziv: df < 0 ? b.ob.izracunDfIzguba : b.ob.izracunDfDobicek, vrednost: znesek(String(Math.abs(df))) },
    { naziv: b.ob.izracunF, vrednost: znesek(t.f) },
    { naziv: b.ob.izracunBe, vrednost: t.be === null ? '—' : znesek(t.be) },
    { naziv: b.ob.izracunKontrola, vrednost: znesek(i.cd) },
  ];

  return (
    <section className="kz-karta ob-tocka" aria-label={b.ob.tockaNaslov}>
      <h2>{b.ob.tockaNaslov}</h2>
      <div className="ob-stevilke">
        <div className="kz-podatek">
          <span className="kz-podatek-naziv">{b.ob.tocka}</span>
          <span className="ob-stevilka serif">{t.be === null ? '—' : evri(t.be)}</span>
        </div>
        <div className="kz-podatek">
          <span className="kz-podatek-naziv">{b.ob.cistiPrihodki}</span>
          <span className="ob-stevilka serif">{evri(i.cp)}</span>
        </div>
        <div className="kz-podatek">
          <span className="kz-podatek-naziv">{b.ob.rezerva}</span>
          <span className="ob-stevilka serif">{t.rezerva === null ? '—' : evri(t.rezerva)}</span>
          {t.rezervaDelez !== null && <span className="kz-podatek-naziv">{b.ob.cistihPrihodkov(odstotekDeleza(t.rezervaDelez, 2))}</span>}
        </div>
      </div>
      <p className="ob-stavek">{stavek}</p>
      <div className={odprt ? 'ob-graf-in-izracun odprt' : 'ob-graf-in-izracun'}>
        <div className="ob-graf">
          {t.stanje !== 'ni_mogoce' && <GrafTockePreloma izracun={i} />}
          <p className="kz-karta-podnaslov">{b.ob.podGrafom}</p>
          <p className="kz-karta-podnaslov">{b.ob.opomba}</p>
          <div>
            <button
              type="button"
              className="povezava-gumb"
              aria-expanded={odprt}
              onClick={() => {
                setOdprt(!odprt);
              }}
            >
              {odprt ? b.ob.skrijIzracun : b.ob.poglejIzracun}
            </button>
          </div>
        </div>
        {odprt && (
          <div className="ob-izracun">
            <table className="ob-tabela">
              <tbody>
                {vrstice.map((v) => (
                  <tr key={v.naziv} className={v.podvrstica === true ? 'podvrstica' : undefined}>
                    <td>{v.naziv}</td>
                    <td className="stevilka">{v.vrednost}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <StroskiPoKontih stroski={i.stroski} />
          </div>
        )}
      </div>
    </section>
  );
}

/** Stroški po kontih z deležem. */
function StroskiPoKontih({ stroski }: { stroski: StrosekObcutljivosti[] }): JSX.Element {
  const b = useBesedila();
  return (
    <>
      <h3 className="ob-podnaslov">{b.ob.stroskiPoKontih}</h3>
      <table className="ob-tabela ob-konti">
        <thead>
          <tr>
            <th>{b.ob.konto}</th>
            <th className="stevilka">{b.ob.znesek}</th>
            <th className="stevilka">{b.ob.vc}</th>
          </tr>
        </thead>
        <tbody>
          {stroski.map((s) => (
            <tr key={s.konto}>
              <td>
                <code>{s.konto}</code> {s.naziv}
              </td>
              <td className="stevilka">
                {znesek(s.znesek)}
              </td>
              <td className="stevilka">{s.vrsta === 'nerazvrsceno' ? b.ob.nerazvrsceno : odstotekDeleza(s.delez, 0)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

/**
 * Graf točke preloma (6): vodoravno čisti prihodki od 0 do
 * max(1,3·CP, 1,2·BE); prihodki `y = x`, potrebno kritje `y = F + (V/CP)·x`,
 * fiksni del `y = F` črtkano; presečišče »Točka preloma«, navpična oznaka
 * pri dejanskih prihodkih.
 */
function GrafTockePreloma({ izracun: i }: { izracun: IzracunObcutljivosti }): JSX.Element {
  const b = useBesedila();
  const telefon = useTelefon();
  const cp = Number(i.cp);
  const f = Number(i.tocka.f);
  const naklon = Number(i.variabilni) / cp;
  const be = Number(i.tocka.be ?? 0);
  const xmax = Math.max(1.3 * cp, 1.2 * be);
  const kritje = (x: number): number => f + naklon * x;
  const ymin = Math.min(0, f);
  const ymax = Math.max(xmax, kritje(xmax));
  const W = telefon ? 360 : 720;
  const H = telefon ? 260 : 320;
  const pisava = telefon ? 11 : 12;
  const L = telefon ? 46 : 60;
  const R = 16;
  const T = 22;
  const B = 30;
  const x = (v: number): number => L + (v / xmax) * (W - L - R);
  const y = (v: number): number => T + (1 - (v - ymin) / (ymax - ymin)) * (H - T - B);
  const kx = korak(xmax);
  const ky = korak(ymax - ymin);
  const osX: number[] = [];
  for (let v = 0; v <= xmax + 1e-6; v += kx) osX.push(v);
  const osY: number[] = [];
  for (let v = Math.ceil(ymin / ky) * ky; v <= ymax + 1e-6; v += ky) osY.push(v);
  const levoOdCp = x(cp) > W * 0.6;
  return (
    <figure className="ob-graf-slika">
      <div className="ob-legenda">
        <span>
          <i className="ob-crta prihodki" /> {b.ob.legendaPrihodki}
        </span>
        <span>
          <i className="ob-crta kritje" /> {b.ob.legendaKritje}
        </span>
        <span>
          <i className="ob-crta fiksni" /> {b.ob.legendaFiksni}
        </span>
      </div>
      <svg viewBox={`0 0 ${String(W)} ${String(H)}`} role="img" aria-label={b.ob.grafOpis} className="ob-svg">
        {osY.map((v) => (
          <g key={`y${String(v)}`}>
            <line x1={L} x2={W - R} y1={y(v)} y2={y(v)} className="ob-mreza" />
            <text x={L - 6} y={y(v) + 4} textAnchor="end" fontSize={pisava} className="ob-os">
              {kratko(v)}
            </text>
          </g>
        ))}
        {osX.map((v) => (
          <text key={`x${String(v)}`} x={x(v)} y={H - B + 16} textAnchor="middle" fontSize={pisava} className="ob-os">
            {kratko(v)}
          </text>
        ))}
        <line x1={x(0)} y1={y(f)} x2={x(xmax)} y2={y(f)} className="ob-fiksni" />
        <line x1={x(0)} y1={y(kritje(0))} x2={x(xmax)} y2={y(kritje(xmax))} className="ob-kritje" />
        <line x1={x(0)} y1={y(0)} x2={x(xmax)} y2={y(xmax)} className="ob-prihodki" />
        <line x1={x(cp)} x2={x(cp)} y1={T} y2={H - B} className="ob-dejanski" />
        <text x={levoOdCp ? x(cp) - 6 : x(cp) + 6} y={T - 6} textAnchor={levoOdCp ? 'end' : 'start'} fontSize={pisava} className="ob-dejanski-napis">
          {`${b.ob.dejanskiPrihodki} ${kratko(cp)}`}
        </text>
        {/* Pri »pokrito« (F ≤ 0) se črti pri pozitivnih prihodkih ne sekata: točke ni. */}
        {i.tocka.stanje !== 'pokrito' && (
          <>
            <circle cx={x(be)} cy={y(be)} r={5} className="ob-tocka-kroglica" />
            <text x={x(be) - 8} y={y(be) - 10} textAnchor="end" fontSize={pisava + 1} className="ob-tocka-napis">
              {`${b.ob.tockaNaGrafu} ${kratko(be)}`}
            </text>
          </>
        )}
      </svg>
      {i.tocka.stanje === 'pokrito' && <figcaption className="kz-karta-podnaslov">{b.ob.grafPokrito}</figcaption>}
    </figure>
  );
}

function MatrikaEbitda({
  izracun: i,
  nacin,
  naNacin,
  naStrosek,
}: {
  izracun: IzracunObcutljivosti;
  nacin: NacinMatrike;
  naNacin: (n: NacinMatrike) => void;
  naStrosek: (konto: string) => void;
}): JSX.Element {
  const b = useBesedila();
  // Izbira celice nosi s seboj, za kateri izračun, strošek in način velja.
  // Ob novem izračunu, drugem strošku ali načinu se **med izrisom** trajno
  // izbriše (ponastavitev lastnega stanja med izrisom, kot priporoča React),
  // ne z učinkom: učinek se po prvem izrisu izvede šele ob naslednjem premoru
  // in lahko povozi klik, ki je prišel tik pred njim. Zgolj skrivanje ne bi
  // zadoščalo - ob vrnitvi na prejšnji način bi se stara izbira spet odprla.
  const [izbor, setIzbor] = useState<{
    za: { izracun: IzracunObcutljivosti; konto: string; nacin: NacinMatrike };
    vrstica: number;
    stolpec: number;
  } | null>(null);
  const m = nacin === 'kolicina' ? i.matrika.kolicina : i.matrika.cena;
  const izhodisce = Number(i.ebitda);
  const izb = i.izbrani;
  const nazivIzbranega = `${izb.konto} ${izb.naziv}`;
  const srednja = (x: string): boolean => Number(x) === 0;
  const izborVelja = izbor !== null && izbor.za.izracun === i && izbor.za.konto === izb.konto && izbor.za.nacin === nacin;
  if (izbor !== null && !izborVelja) setIzbor(null);
  const izbrana = izborVelja ? { vrstica: izbor.vrstica, stolpec: izbor.stolpec } : null;
  const setIzbrana = (nova: { vrstica: number; stolpec: number } | null): void => {
    setIzbor(nova === null ? null : { za: { izracun: i, konto: izb.konto, nacin }, ...nova });
  };
  const celica: CelicaMatrike | null = izbrana === null ? null : (m[izbrana.vrstica]?.[izbrana.stolpec] ?? null);
  const kontiIzbranega = i.stroski.filter((s) => (izb.raven === 2 ? s.konto2 === izb.konto : s.konto === izb.konto));

  return (
    <section className="kz-karta ob-matrika" aria-label={b.ob.matrikaNaslov}>
      <h2>{b.ob.matrikaNaslov}</h2>
      <div className="ob-kontrole">
        <label className="kz-kontrola">
          <span className="kz-kontrola-naziv">{b.ob.strosek}</span>
          <select
            value={izb.konto}
            onChange={(e) => {
              naStrosek(e.target.value);
            }}
          >
            {i.moznosti.map((x) => (
              <option key={x.konto} value={x.konto}>
                {`${x.raven === 3 ? '   ' : ''}${x.konto} ${x.naziv} · ${b.ob.variabilniDel(odstotekDeleza(x.variabilniDel, 0))}`}
              </option>
            ))}
          </select>
        </label>
        <div className="stikalo-pogleda" role="group" aria-label={b.ob.matrikaNaslov}>
          <button
            type="button"
            aria-pressed={nacin === 'kolicina'}
            onClick={() => {
              naNacin('kolicina');
            }}
          >
            {b.ob.nacinKolicina}
          </button>
          <button
            type="button"
            aria-pressed={nacin === 'cena'}
            onClick={() => {
              naNacin('cena');
            }}
          >
            {b.ob.nacinCena}
          </button>
        </div>
      </div>
      <p className="kz-karta-podnaslov">{nacin === 'kolicina' ? b.ob.razlagaKolicina : b.ob.razlagaCena}</p>
      <p className="ob-stavek">
        {`${b.ob.strosek}: `}
        <strong>{b.ob.strosekIzbran(nazivIzbranega, odstotekDeleza(izb.variabilniDel, 0))}</strong>
      </p>
      {Number(izb.variabilniDel) === 0 && <p className="opozorilo">{b.ob.vseFiksno}</p>}
      <div className="ob-drsnik">
        <table className="ob-mreza-tabela">
          <thead>
            <tr>
              <th />
              <th colSpan={i.matrika.stolpci.length} className="ob-os-naslov">
                {b.ob.osStroska(nazivIzbranega)}
              </th>
            </tr>
            <tr>
              <th className="ob-os-naslov levo">{nacin === 'kolicina' ? b.ob.osKolicina : b.ob.osCena}</th>
              {i.matrika.stolpci.map((d) => (
                <th key={d} className="stevilka">
                  {sprememba(d)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {m.map((vrstica, r) => (
              <tr key={i.matrika.vrstice[r]}>
                <th className="stevilka">{sprememba(i.matrika.vrstice[r] ?? '0')}</th>
                {vrstica.map((c, s) => {
                  const sredina = srednja(i.matrika.vrstice[r] ?? '') && srednja(i.matrika.stolpci[s] ?? '');
                  const r2 = Number(c.ebitda) - izhodisce;
                  const izbranaTa = izbrana?.vrstica === r && izbrana.stolpec === s;
                  return (
                    <td key={i.matrika.stolpci[s]} className={`${sredina ? 'sredina' : ''}${izbranaTa ? ' izbrana' : ''}`}>
                      <button
                        type="button"
                        aria-pressed={izbranaTa}
                        onClick={() => {
                          setIzbrana(izbranaTa ? null : { vrstica: r, stolpec: s });
                        }}
                      >
                        <span className="ob-celica-ebitda serif">{evri(c.ebitda)}</span>
                        <span className={`ob-celica-razlika ${r2 > 0.5 ? 'boljse' : r2 < -0.5 ? 'slabse' : ''}`}>
                          {sredina ? b.ob.izhodisce : razlika(r2)}
                        </span>
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="kz-karta-podnaslov">{b.ob.podMatriko(evri(i.ebitda))}</p>
      {izbrana !== null && celica !== null && (
        <div className="ob-izracun ob-izracun-celice">
          <div className="ob-izracun-glava">
            <h3 className="ob-podnaslov">
              {b.ob.celicaNaslov(sprememba(i.matrika.vrstice[izbrana.vrstica] ?? '0'), sprememba(i.matrika.stolpci[izbrana.stolpec] ?? '0'))}
            </h3>
            <button
              type="button"
              className="povezava-gumb"
              onClick={() => {
                setIzbrana(null);
              }}
            >
              {b.ob.zapri}
            </button>
          </div>
          <table className="ob-tabela">
            <tbody>
              <tr>
                <td>{b.ob.celicaPrihodki(faktor(i.matrika.vrstice[izbrana.vrstica] ?? '0'))}</td>
                <td className="stevilka">
                  {znesek(celica.prihodki)}
                </td>
              </tr>
              <tr className="podvrstica">
                <td>{b.ob.celicaIzhodisce}</td>
                <td className="stevilka">{znesek(i.cp)}</td>
              </tr>
              <tr>
                <td>{b.ob.celicaOr}</td>
                <td className="stevilka">
                  {znesek(i.or)}
                </td>
              </tr>
              <tr>
                <td>{b.ob.celicaFiksni}</td>
                <td className="stevilka">{znesek(i.fiksni)}</td>
              </tr>
              <tr>
                <td>{b.ob.celicaOstali}</td>
                <td className="stevilka">{znesek(celica.variabilniOstali)}</td>
              </tr>
              <tr>
                <td>{`${b.ob.celicaIzbrani} (${nazivIzbranega})`}</td>
                <td className="stevilka">{znesek(celica.variabilniIzbrani)}</td>
              </tr>
              <tr className="vsota">
                <td>{b.ob.celicaEbitda}</td>
                <td className="stevilka">{znesek(celica.ebitda)}</td>
              </tr>
            </tbody>
          </table>
          <StroskiPoKontih stroski={kontiIzbranega} />
        </div>
      )}
    </section>
  );
}
